import { useState, FormEvent } from 'react';
import { supabase } from '../lib/supabase';

interface AuthFormProps {
  onAuthSuccess: () => void;
}

export function AuthForm({ onAuthSuccess }: AuthFormProps) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);
    
    try {
      if (isSignUp) {
        // Check if username is taken
        const { data: existingUser } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('username', username.toLowerCase().trim())
          .single();
        
        if (existingUser) {
          throw new Error('Username is already taken');
        }
        
        // Sign up with username in metadata - trigger will create profile
        const { error } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            data: {
              username: username.toLowerCase().trim()
            }
          }
        });
        if (error) throw error;
        
        alert('Check your email for confirmation link!');
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        onAuthSuccess();
      }
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-6 safe-top safe-bottom"
      style={{ background: '#2a2520' }}
    >
      {/* Logo / Branding */}
      <div className="text-center mb-10">
        <div className="text-5xl mb-3">🍷</div>
        <h1 className="wine-title mb-2" style={{ fontSize: '36px', color: '#f5f1e8' }}>
          CellarLog
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '16px' }}>
          Your personal wine cellar
        </p>
      </div>

      {/* Auth Card */}
      <div 
        className="w-full max-w-sm rounded-2xl p-6"
        style={{ 
          background: '#f5f1e8', 
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)' 
        }}
      >
        {/* Toggle */}
        <div 
          className="flex rounded-lg p-1 mb-5"
          style={{ background: '#ebe7de' }}
        >
          <button
            type="button"
            onClick={() => setIsSignUp(false)}
            className="flex-1 py-2.5 rounded-md font-semibold transition-all"
            style={{ 
              background: !isSignUp ? 'white' : 'transparent',
              color: !isSignUp ? '#722f37' : '#8b7d6b',
              fontSize: '15px',
              boxShadow: !isSignUp ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            Log In
          </button>
          <button
            type="button"
            onClick={() => setIsSignUp(true)}
            className="flex-1 py-2.5 rounded-md font-semibold transition-all"
            style={{ 
              background: isSignUp ? 'white' : 'transparent',
              color: isSignUp ? '#722f37' : '#8b7d6b',
              fontSize: '15px',
              boxShadow: isSignUp ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            Sign Up
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {isSignUp && (
            <div>
              <label className="ios-label">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Choose a username"
                className="ios-input"
                autoComplete="username"
                autoCapitalize="none"
                required={isSignUp}
                minLength={3}
                maxLength={20}
                pattern="[a-zA-Z0-9_]+"
                title="Letters, numbers, and underscores only"
              />
            </div>
          )}
          
          <div>
            <label className="ios-label">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="ios-input"
              autoComplete="email"
              autoCapitalize="none"
              required
            />
          </div>
          
          <div>
            <label className="ios-label">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="ios-input"
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              required
            />
          </div>
          
          {authError && (
            <p className="text-sm" style={{ color: '#dc2626' }}>{authError}</p>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="ios-btn ios-btn-primary w-full"
            style={{ marginTop: '20px' }}
          >
            {loading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Log In')}
          </button>
        </form>

        {/* Forgot Password - sign in only */}
        {!isSignUp && (
          <div className="text-center mt-4">
            <button 
              type="button"
              className="text-sm"
              style={{ color: '#722f37' }}
              onClick={() => {
                if (email) {
                  supabase.auth.resetPasswordForEmail(email);
                  alert('If an account exists, a password reset link has been sent.');
                } else {
                  alert('Please enter your email first.');
                }
              }}
            >
              Forgot password?
            </button>
          </div>
        )}
      </div>

      {/* Build Version */}
      <p style={{ 
        marginTop: '24px',
        fontSize: '10px', 
        color: 'rgba(255,255,255,0.3)'
      }}>
        Build: {new Date(__BUILD_TIME__).toLocaleString()}
      </p>
    </div>
  );
}
